// src/services/areaScheduleService.ts
import { Area, Gathering, GatheringStatus } from '../types';

// Interface for booked time slots
export interface BookedSlot {
  start_time: string;
  end_time: string;
  gathering_title?: string;
  gathering_id?: string;
  status?: GatheringStatus;
}

// Interface for area schedule
export interface AreaSchedule {
  id: string;
  name: string;
  capacity: number;
  space: string;
  booked_slots: BookedSlot[];
}

/**
 * Transforms area and gathering data into area schedules for the timeline view
 * @param areas Array of areas for a space
 * @param gatherings Array of gatherings for a date
 * @param spaceName Name of the space
 * @returns Array of area schedules with booked slots
 */
export const transformToAreaSchedules = (
  areas: Area[],
  gatherings: Gathering[],
  spaceName: string = "Unknown Space"
): AreaSchedule[] => {
  // Create area schedules with empty booked slots
  const areaSchedules: AreaSchedule[] = areas.map(area => ({
    id: area.id,
    name: area.name,
    capacity: area.capacity,
    space: spaceName,
    booked_slots: []
  }));
  
  // Add booked slots to each area
  gatherings.forEach(gathering => {
    if (gathering.areas && gathering.areas.length > 0) {
      gathering.areas.forEach(gatheringArea => {
        const areaSchedule = areaSchedules.find(a => a.id === gatheringArea.id);
        if (areaSchedule) {
          areaSchedule.booked_slots.push({
            start_time: gathering.start_time,
            end_time: gathering.end_time,
            gathering_title: gathering.title,
            gathering_id: gathering.id,
            status: gathering.status
          });
        }
      });
    }
  });
  
  return areaSchedules;
};

/**
 * Fetches area schedules for a specific date and space
 * @param fetchAreas Function to fetch areas
 * @param fetchGatherings Function to fetch gatherings
 * @param token Firebase authentication token
 * @param date Date in YYYY-MM-DD format
 * @param spaceId Space ID
 * @returns Promise containing area schedules
 */
export const fetchAreaSchedules = async (
  fetchAreas: (token: string, spaceId: string) => Promise<Area[]>,
  fetchGatherings: (token: string, fromDate: string, toDate: string, spaceId: string | null) => Promise<Gathering[]>,
  token: string,
  date: string,
  spaceId: string,
  spaceName: string = "Unknown Space"
): Promise<AreaSchedule[]> => {
  try {
    // Fetch areas for the space
    const areas = await fetchAreas(token, spaceId);
    
    // Fetch gatherings for the date
    const gatherings = await fetchGatherings(token, date, date, spaceId);
    
    // Transform data into area schedules
    return transformToAreaSchedules(areas, gatherings, spaceName);
  } catch (error) {
    console.error('Error fetching area schedules:', error);
    throw error;
  }
};
